import { HourNumbers, MinuteNumbers, WeekdayNumbers } from 'luxon';
import { duration } from '../utils/duration';
import { tuple } from '../utils/types';

export enum NotificationSettings {
  Off = 'off',
  Less = 'less',
  On = 'on',
}

const richMenuNames = ['miniapp', 'miniappCompact'] as const;
const chatbotEnv = ['dev', 'prod'] as const;

export type RichMenuName = (typeof richMenuNames)[number];
export type ChatbotEnv = (typeof chatbotEnv)[number];

export const richMenuIds: Record<ChatbotEnv, Record<RichMenuName, string>> = {
  dev: {
    miniapp: 'richmenu-59a22f2021d9c3644c5d2c0c00121b4b',
    miniappCompact: 'richmenu-356b54185368352e29afa8189a36d332',
  },
  prod: {
    miniapp: 'richmenu-7e6f747f216a52b1408a327c0892d8dd',
    miniappCompact: 'richmenu-525cd203bf97dd9dfd075a0f04f2a180',
  },
};

/** The OA's default rich menu, shown to users with no per-user rich menu. */
export const oaDefaultRichMenu: RichMenuName = 'miniapp';

/**
 * Accounts created before this time are excluded from the compact rich menu
 * test. It must not precede the release: an account created in between may
 * already be subscribed, and a later unblock would enroll it as a new
 * subscriber.
 */
export const compactRichMenuTestStartAt = new Date(
  '2026-10-01T13:00:00+09:00',
).getTime();

export type ChatbotGroup = 'teen' | 'newAdult' | 'oldAdult';
export type ChatMessageType =
  | 'greetings'
  | 'status'
  | 'visit'
  | 'energy'
  | 'duo'
  | 'reengagement'
  | 'event'
  | 'daycare'
  | 'liveOps';

// The duration an adult is considered a newAdult since install in days
export const daysConsideredNew = 7;
// Resubscription
/** Persisted lifecycle markers used by legacy and current OA reward flows. */
export type ResubscriptionState = 'pending' | 'completed' | 'claimed';
export const resubscriptionGemsReward = 50;
/** Puzzle level that unlocks the incentivized OA treatment. */
export const incentivizedOAStartLevel = 5;
/** Puzzle level that triggers the persistent incentivized OA reminder. */
export const incentivizedOAReminderLevel = 15;
/** One-time gems granted after verified OA addition in A/B 0489. */
export const incentivizedOAAdditionGemsReward = 200;

/** Entry points that can offer the lifetime OA incentive. */
export type IncentivizedOAOrigin = 'launch_sequence' | 'level_5' | 'level_15';

/** Results returned by the client-verified incentivized OA reward claim. */
export const incentivizedOARewardClaimResults = tuple(
  'granted',
  'already_claimed',
  'not_subscribed',
  'ineligible',
);
/** Result returned by the client-verified incentivized OA reward claim. */
export type IncentivizedOARewardClaimResult =
  (typeof incentivizedOARewardClaimResults)[number];
export const resubscriptionPromptCooldown = duration({ days: 1 }); // LiveOps
export const liveOpsMutePeriod = duration({ days: 7 });
export const liveOpsEventType = tuple(
  'fuji',
  'matchLife',
  'waterballoon',
  'moveLikeBuruburu',
  'lollipopWednesday',
  'streakChampion',
  'yellowFestival',
  'miniFirecracker',
  'tournamentStart',
  'tournamentTomorrow',
  'tournamentSoon',
  'tournamentEnd',
  'lampsStart',
  'lampsEnd',
  // Keep the retired value valid for queued scheduled actions.
  'turtleRace',
  'dreamDrift',
);

export type LiveOpsEventType = (typeof liveOpsEventType)[number];

type LiveOpsEvent = {
  day: WeekdayNumbers;
  time: { hour: HourNumbers; minute: MinuteNumbers };
  type: LiveOpsEventType;
};

// NB: these must be kept in order and day+type should be unique
export const liveOpsSchedule: LiveOpsEvent[] = [
  // Monday
  {
    day: 1,
    time: { hour: 10, minute: 19 },
    type: 'waterballoon',
  },
  {
    day: 1,
    time: { hour: 17, minute: 0 },
    type: 'lampsStart',
  },
  {
    day: 1,
    time: { hour: 22, minute: 0 },
    type: 'lampsEnd',
  },
  // Tuesday
  {
    day: 2,
    time: { hour: 10, minute: 19 },
    type: 'moveLikeBuruburu',
  },
  {
    day: 2,
    time: { hour: 22, minute: 0 },
    type: 'tournamentSoon',
  },
  {
    day: 2,
    time: { hour: 23, minute: 0 },
    type: 'tournamentEnd',
  },
  {
    day: 2,
    time: { hour: 23, minute: 3 },
    type: 'tournamentStart',
  },
  // Wednesday
  {
    day: 3,
    time: { hour: 10, minute: 19 },
    type: 'lollipopWednesday',
  },
  {
    day: 3,
    time: { hour: 22, minute: 0 },
    type: 'tournamentTomorrow',
  },
  // Thursday
  {
    day: 4,
    time: { hour: 10, minute: 19 },
    type: 'streakChampion',
  },
  {
    day: 4,
    time: { hour: 22, minute: 0 },
    type: 'tournamentSoon',
  },
  {
    day: 4,
    time: { hour: 23, minute: 0 },
    type: 'tournamentEnd',
  },
  {
    day: 4,
    time: { hour: 23, minute: 3 },
    type: 'tournamentStart',
  },
  // Friday
  {
    day: 5,
    time: { hour: 10, minute: 19 },
    type: 'yellowFestival',
  },
  {
    day: 5,
    time: { hour: 22, minute: 0 },
    type: 'dreamDrift',
  },
  // Saturday
  {
    day: 6,
    time: { hour: 10, minute: 19 },
    type: 'miniFirecracker',
  },
  {
    day: 6,
    time: { hour: 22, minute: 0 },
    type: 'tournamentTomorrow',
  },
  // Sunday
  {
    day: 7,
    time: { hour: 10, minute: 19 },
    type: 'matchLife',
  },
  {
    day: 7,
    time: { hour: 22, minute: 0 },
    type: 'tournamentSoon',
  },
  {
    day: 7,
    time: { hour: 23, minute: 0 },
    type: 'tournamentEnd',
  },
  {
    day: 7,
    time: { hour: 23, minute: 3 },
    type: 'tournamentStart',
  },
];

/**
 * Auto response for control users or if feature disabled
 */
export const autoResponse = {
  en: (url: string) =>
    [
      `Thank you for the message!`,
      ``,
      `To play the game, tap the menu at the bottom of the screen or this URL!`,
      ``,
      `${url}?payload=%7B%22%24channel%22%3A%22CHATBOT%22%2C%22feature%22%3A%22auto_response_chat%22%7D`,
    ].join('\n'),
  ja: (url: string) =>
    [
      `メッセージありがとう！`,
      ``,
      `ゲームをプレイするには、画面下のメニューか、このURLをタップしてね！`,
      ``,
      `${url}?payload=%7B%22%24channel%22%3A%22CHATBOT%22%2C%22feature%22%3A%22auto_response_chat%22%7D`,
    ].join('\n'),
};
